import {
  AfterContentInit,
  Component,
  contentChildren,
  effect,
  ElementRef,
  InjectionToken,
  input,
  NgZone,
  inject,
  computed,
  signal,
} from '@angular/core';
import { MatListItem } from '@angular/material/list';
import { Platform } from '@angular/cdk/platform';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MediaService } from '@exl-ng/mulo-core';
import { MatMenuModule } from '@angular/material/menu';
import { CdkObserveContent } from '@angular/cdk/observers';
import { StatusTagComponent } from '../status-tag';
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle';
import { TranslatePipe } from '@ngx-translate/core';
import { VisibilitySwitchComponent } from '../visibility-switch';
import { MultipleEventsDirective } from '../../directives';

/**
 * An interface and InjectionToken to prevent Circular dependency of ActionListItemComponent and ActionListItemActionComponent
 * Becuase parent component ActionListItemComponent hold contentChildren of child ActionListItemActionComponent
 * and ActionListItemActionComponent inject ActionListItemComponent
 */
export interface MuloActionListItem {}

export const MULO_ACTION_LIST_ITEM = new InjectionToken<MuloActionListItem>(
  'MULO_ACTION_LIST_ITEM',
);

export interface MuloActionListItemAction {
  type: 'visibility' | 'icon-btn' | 'toggle' | 'button';
  data?: any;
  icon?: string;
  rtlMirrorIcon?: boolean;
  labels?: string[];
  disabled?: boolean;
  keepVisible?: boolean;
  actionEvent?: Function;
}

@Component({
  selector: 'mulo-action-list-item',
  templateUrl: './action-list-item.component.html',
  styleUrls: ['./action-list-item.component.scss'],
  host: {
    '[class.is-hidden]': 'hidden()',
    '[class.is-hover]': 'showOnHover()',
  },
  providers: [
    {
      provide: MULO_ACTION_LIST_ITEM,
      useExisting: ActionListItemComponent,
    },
  ],
  imports: [
    StatusTagComponent,
    CdkObserveContent,
    MatMenuModule,
    MatButtonModule,
    MatIcon,
    MatSlideToggle,
    TranslatePipe,
    VisibilitySwitchComponent,
    MultipleEventsDirective,
  ],
})
export class ActionListItemComponent
  extends MatListItem
  implements MuloActionListItem, AfterContentInit
{
  get disabled(): boolean {
    return this._disabledSignal();
  }

  set disabled(value: boolean) {
    this._disabledSignal.set(value);
    super.disabled = value;
  }
  media = inject(MediaService);

  hidden = input(false);
  hiddenLabel = input('Hidden');
  hiddenDescription = input('');
  showOnHover = input(false);
  moreBtnLabel = input('More Options');
  actions = input<MuloActionListItemAction[]>([]);
  mainActionCount = computed(() => this.menuActions().length);
  useMenu = computed(() => this.media.state()?.['lt-md']);
  _disabledSignal = signal(false);
  visibleActions = computed(() =>
    this.useMenu()
      ? this.actions().filter((action) => action?.keepVisible === true)
      : this.actions(),
  );
  menuActions = computed(() =>
    this.actions().filter((action) => action?.keepVisible !== true),
  );

  updateItemLines = super._updateItemLines;

  constructor() {
    const _element = inject<ElementRef<HTMLElement>>(ElementRef);
    const _ngZone = inject(NgZone);
    const _platform = inject(Platform);

    super(_element, _ngZone, null, _platform);

    effect(() => {
      const hidden = this.hidden();
      if (hidden !== undefined) {
        this.recalcLinesForHidden();
      }
    });
  }

  ngAfterContentInit() {
    // Disable the item if all action buttons disabled
    this._disabledSignal.update(
      (prev) =>
        prev ||
        (this.actions()?.length > 0 &&
          !this.actions().some((action) => !action.disabled)),
    );
    this.recalcLinesForHidden();
    this._lines.changes.subscribe(() => this.recalcLinesForHidden());
    this._titles.changes.subscribe(() => this.recalcLinesForHidden());
  }

  recalcLinesForHidden() {
    if (this.hidden() === true) {
      this.lines = this.inferLinesFromContent() + 1;
    } else {
      this.lines = this.inferLinesFromContent();
    }
  }

  private inferLinesFromContent() {
    let numOfLines = this._titles?.length + this._lines?.length;
    if (this._hasUnscopedTextContent) {
      numOfLines += 1;
    }
    return numOfLines;
  }

  handleActionEvent(action: MuloActionListItemAction, ...args: any[]) {
    if (action.actionEvent && typeof action.actionEvent === 'function') {
      action.actionEvent(...args);
    }
  }

  handleMenuToggle(
    eventData: MouseEvent,
    toggleElem: MatSlideToggle,
    action: MuloActionListItemAction,
  ) {
    eventData.stopPropagation();
    const change: MatSlideToggleChange = {
      checked: !toggleElem.checked,
      source: toggleElem,
    };
    this.handleActionEvent(action, change);
  }
}
